// ============================================
// src/components/payments/PaymentReminders.js
// Enhanced with AI-powered message generation
// ============================================

import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase.config';
import aiGenerator from '../../services/NetlifyAIGenerator';
  import notificationService from '../../services/notification.service';
  
function PaymentReminders({ residents, payments, currentMonth, currentYear }) {
  const [message, setMessage] = useState('');
  const [gpayNumber, setGpayNumber] = useState('9884723782');
  const [maintenanceAmount, setMaintenanceAmount] = useState(800);
  const [loading, setLoading] = useState(false);
  const [paymentStatuses, setPaymentStatuses] = useState({});
  const [reminderHistory, setReminderHistory] = useState([]);
  const [messageStyle, setMessageStyle] = useState('standard');
  const [customTemplate, setCustomTemplate] = useState('');

// Remove or comment out the AI-related code in PaymentReminders.js
// Replace the generateAIMessage function with:

const generateAIMessage = async () => {
  setLoading(true);
  
  // Use template-based generation instead of AI
  const totalFlats = Object.keys(paymentStatuses).length;
  const paidFlats = Object.values(paymentStatuses).filter(status => status === 'Paid').length;
  const pendingFlats = totalFlats - paidFlats;
  
  let flatDetailsString = '';
  Object.keys(paymentStatuses).forEach(flatNumber => {
    const status = paymentStatuses[flatNumber] || '';
    flatDetailsString += `${flatNumber} – ${status}\n`;
  });
  
  // Use a smart template instead of AI
  const smartTemplate = messageTemplates[messageStyle] || messageTemplates.standard;
  const generatedMessage = smartTemplate.template({
    amount: maintenanceAmount,
    month: new Date(currentYear, currentMonth - 1).toLocaleDateString('en-US', { month: 'long' }),
    year: currentYear,
    paidCount: paidFlats,
    pendingCount: pendingFlats,
    flatDetails: flatDetailsString.trim(),
    gpay: gpayNumber,
    totalFlats,
    percentage: Math.round((paidFlats / totalFlats) * 100),
    collected: paidFlats * maintenanceAmount,
    expected: totalFlats * maintenanceAmount
  });
  
  setMessage(generatedMessage);
  setLoading(false);
};

  // Predefined message templates
  const messageTemplates = {
    standard: {
      name: "Standard Format",
      template: (data) => `The monthly maintenance charge of ₹${data.amount} started in ${data.month} ${data.year}.
Payment Received Details!
${data.flatDetails}
Payment can be made via GPay to ${data.gpay}`
    },
    
    friendly: {
      name: "Friendly Reminder",
      template: (data) => `Hello Everyone! 👋

Hope you're all doing well! Just a gentle reminder about our monthly maintenance fee of ₹${data.amount} for ${data.month} ${data.year}.

✅ Payment Status:
${data.flatDetails}

💳 Payment Info:
• Amount: ₹${data.amount}
• GPay: ${data.gpay}
• Please mention your flat number when paying

Thank you for your cooperation! 🙏
Have a great day!`
    },
    
    formal: {
      name: "Formal Notice",
      template: (data) => `MAINTENANCE PAYMENT NOTICE

Dear Residents,

This is to inform you that the monthly maintenance fee of ₹${data.amount} for ${data.month} ${data.year} is now due.

PAYMENT STATUS:
${data.flatDetails}

Payment Details:
- Amount Due: ₹${data.amount}
- Payment Method: GPay
- Account Number: ${data.gpay}
- Due Date: 5th of ${data.month}

Please ensure timely payment to maintain uninterrupted services.

Regards,
Society Management`
    },
    
    urgent: {
      name: "Urgent Reminder",
      template: (data) => `🚨 URGENT PAYMENT REMINDER 🚨

Maintenance Fee: ₹${data.amount}
Month: ${data.month} ${data.year}

⚠️ PAYMENT STATUS:
${data.flatDetails}

${data.pendingCount > 0 ? `📌 ${data.pendingCount} flats have pending payments!` : '✅ All payments received!'}

IMMEDIATE ACTION REQUIRED:
Please pay via GPay: ${data.gpay}

Note: Late fees may apply after the 10th.`
    },
    
    appreciation: {
      name: "Appreciation Message",
      template: (data) => `Dear Residents,

Thank you to all who have promptly paid the maintenance fee of ₹${data.amount} for ${data.month} ${data.year}! 

Payment Update:
${data.flatDetails}

${data.paidCount > 0 ? `🎉 Special thanks to ${data.paidCount} flats for timely payment!` : ''}

For pending payments:
GPay: ${data.gpay}

Your cooperation keeps our community running smoothly! 💪`
    },
    
    detailed: {
      name: "Detailed Report",
      template: (data) => `MONTHLY MAINTENANCE - ${data.month.toUpperCase()} ${data.year}

==================================
PAYMENT SUMMARY
==================================
Total Flats: ${data.totalFlats}
Paid: ${data.paidCount} (${data.percentage}%)
Pending: ${data.pendingCount}
Amount per flat: ₹${data.amount}
Total Collected: ₹${data.collected}
Total Expected: ₹${data.expected}

==================================
FLAT-WISE STATUS
==================================
${data.flatDetails}

==================================
PAYMENT INFORMATION
==================================
Method: GPay/PhonePe/Paytm
Number: ${data.gpay}
Reference: Flat Number + Name

Thank you!`
    },
    
    motivational: {
      name: "Motivational",
      template: (data) => `Good Morning/Evening Everyone! ☀️

"A society grows great when residents plant trees whose shade they know they shall never sit in."

Our maintenance contribution of ₹${data.amount} for ${data.month} helps us build a better community together!

Current Status:
${data.flatDetails}

${data.percentage >= 80 ? '🏆 Amazing! We are at ' + data.percentage + '% collection!' : 
  data.percentage >= 50 ? '💪 Good progress! ' + data.percentage + '% collected so far!' :
  '📈 Let\'s work together to reach our goal!'}

Pay via GPay: ${data.gpay}

Together we make our society better! 🏡`
    },
    
    festive: {
      name: "Festive/Seasonal",
      template: (data) => {
        const currentMonth = new Date().getMonth();
        let festiveGreeting = '';
        
        if (currentMonth === 0) festiveGreeting = '🎊 Happy New Year! ';
        else if (currentMonth === 2) festiveGreeting = '🎨 Happy Holi! ';
        else if (currentMonth === 9) festiveGreeting = '🪔 Happy Diwali Season! ';
        else if (currentMonth === 11) festiveGreeting = '🎄 Season\'s Greetings! ';
        else festiveGreeting = '🌟 Greetings! ';
        
        return `${festiveGreeting}

Dear Residents,

As we celebrate this wonderful time, let's also ensure our community responsibilities are met.

Maintenance Fee for ${data.month} ${data.year}: ₹${data.amount}

Status Update:
${data.flatDetails}

Please contribute via GPay: ${data.gpay}

Wishing you and your family happiness and prosperity! ✨`;
      }
    },
    
    reminder_series: {
      name: "Progressive Reminder",
      template: (data) => {
        const dayOfMonth = new Date().getDate();
        
        if (dayOfMonth <= 3) {
          return `📅 FIRST REMINDER - ${data.month} ${data.year}

Maintenance fee of ₹${data.amount} is now open for payment.

Current Status:
${data.flatDetails}

Early bird payment via GPay: ${data.gpay}
Thank you!`;
        } else if (dayOfMonth <= 7) {
          return `📢 SECOND REMINDER - ${data.month} ${data.year}

Gentle reminder: Maintenance ₹${data.amount} is due.

Payment Status:
${data.flatDetails}

Please pay via GPay: ${data.gpay} by the 10th to avoid late fees.`;
        } else {
          return `⚠️ FINAL REMINDER - ${data.month} ${data.year}

URGENT: Maintenance payment of ₹${data.amount} is overdue.

Pending Payments:
${data.flatDetails}

Immediate payment required via GPay: ${data.gpay}
Late fees may apply.`;
        }
      }
    },
    
    weekend: {
      name: "Weekend Reminder",
      template: (data) => `Happy Weekend! 🌈

Hope you're enjoying your time off! Quick update on maintenance payments:

Amount: ₹${data.amount} (${data.month} ${data.year})

Status Check:
${data.flatDetails}

If you haven't paid yet, the weekend is a great time to clear dues!
GPay: ${data.gpay}

Have a wonderful weekend! 😊`
    }
  };

  useEffect(() => {
    generatePaymentStatus();
    fetchReminderHistory();
  }, [residents, payments, currentMonth, currentYear]);

  const generatePaymentStatus = () => {
    const statusMap = {};
    
    const uniqueFlats = [...new Set(residents.map(r => r.flat_number))].sort((a, b) => 
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );

    uniqueFlats.forEach(flatNumber => {
      const flatResidents = residents.filter(r => r.flat_number === flatNumber);
      const hasPaid = flatResidents.some(resident => 
        payments.some(p => 
          p.resident_id === resident.id && 
          p.status === 'paid' &&
          p.payment_month === currentMonth &&
          p.payment_year === currentYear
        )
      );
      statusMap[flatNumber] = hasPaid ? 'Paid' : '';
    });

    setPaymentStatuses(statusMap);
    generateMessage(statusMap, messageStyle);
  };

  const generateMessage = (statusMap = paymentStatuses, style = messageStyle) => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
    const monthName = monthNames[currentMonth - 1];
    
    // Generate flat details string
    let flatDetails = '';
    Object.keys(statusMap).sort((a, b) => 
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    ).forEach(flatNumber => {
      const status = statusMap[flatNumber];
      flatDetails += `${flatNumber} – ${status}\n`;
    });
    
    // Calculate statistics
    const totalFlats = Object.keys(statusMap).length;
    const paidCount = Object.values(statusMap).filter(s => s === 'Paid').length;
    const pendingCount = totalFlats - paidCount;
    const percentage = totalFlats > 0 ? Math.round((paidCount / totalFlats) * 100) : 0;
    const collected = paidCount * maintenanceAmount;
    const expected = totalFlats * maintenanceAmount;
    
    // Data object for templates
    const templateData = {
      amount: maintenanceAmount,
      month: monthName,
      year: currentYear,
      gpay: gpayNumber,
      flatDetails: flatDetails.trim(),
      totalFlats,
      paidCount,
      pendingCount,
      percentage,
      collected,
      expected
    };
    
    // Generate message based on selected style
    let generatedMessage = '';
    if (style === 'custom' && customTemplate) {
      try {
        // Replace placeholders in custom template
        generatedMessage = customTemplate
          .replace(/\{amount\}/g, templateData.amount)
          .replace(/\{month\}/g, templateData.month)
          .replace(/\{year\}/g, templateData.year)
          .replace(/\{gpay\}/g, templateData.gpay)
          .replace(/\{flatDetails\}/g, templateData.flatDetails)
          .replace(/\{totalFlats\}/g, templateData.totalFlats)
          .replace(/\{paidCount\}/g, templateData.paidCount)
          .replace(/\{pendingCount\}/g, templateData.pendingCount)
          .replace(/\{percentage\}/g, templateData.percentage)
          .replace(/\{collected\}/g, templateData.collected)
          .replace(/\{expected\}/g, templateData.expected);
      } catch (error) {
        generatedMessage = messageTemplates.standard.template(templateData);
      }
    } else if (messageTemplates[style]) {
      generatedMessage = messageTemplates[style].template(templateData);
    } else {
      generatedMessage = messageTemplates.standard.template(templateData);
    }
    
    setMessage(generatedMessage);
  };

  const fetchReminderHistory = async () => {
    try {
      const { data, error } = await supabase
        .from('payment_reminders')
        .select('*')
        .eq('month', currentMonth)
        .eq('year', currentYear)
        .order('sent_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setReminderHistory(data || []);
    } catch (error) {
      console.error('Error fetching reminder history:', error);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(message).then(() => {
      alert('Message copied to clipboard! You can now paste it in your WhatsApp group.');
    }).catch(err => {
      console.error('Failed to copy: ', err);
      alert('Failed to copy message. Please select and copy manually.');
    });
  };

  const sendViaWhatsApp = () => {
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://web.whatsapp.com/send?text=${encodedMessage}`, '_blank');
  };

  const saveReminderHistory = async () => {
    setLoading(true);
    try {
      const defaulterCount = Object.values(paymentStatuses).filter(status => status === '').length;
      
      const { error } = await supabase
        .from('payment_reminders')
        .insert([{
          month: currentMonth,
          year: currentYear,
          reminder_type: 'whatsapp_group',
          message: message,
          sent_at: new Date().toISOString(),
          metadata: {
            total_flats: Object.keys(paymentStatuses).length,
            paid_count: Object.values(paymentStatuses).filter(s => s === 'Paid').length,
            pending_count: defaulterCount,
            gpay_number: gpayNumber,
            message_style: messageStyle
          }
        }]);

      if (error) throw error;
      
      alert('Reminder saved to history!');
      fetchReminderHistory();
    } catch (error) {
      console.error('Error saving reminder:', error);
      alert('Failed to save reminder history');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = (flatNumber) => {
    const newStatuses = { ...paymentStatuses };
    newStatuses[flatNumber] = newStatuses[flatNumber] === 'Paid' ? '' : 'Paid';
    setPaymentStatuses(newStatuses);
    generateMessage(newStatuses, messageStyle);
  };

  const handleStyleChange = (style) => {
    setMessageStyle(style);
    generateMessage(paymentStatuses, style);
  };

  // Statistics
  const totalFlats = Object.keys(paymentStatuses).length;
  const paidFlats = Object.values(paymentStatuses).filter(status => status === 'Paid').length;
  const pendingFlats = totalFlats - paidFlats;
  const collectionPercentage = totalFlats > 0 ? Math.round((paidFlats / totalFlats) * 100) : 0;

  // Add this function to your PaymentReminders component
// Add this function to your PaymentReminders component
const sendPushNotifications = async () => {
  // Get monthName from the monthNames array
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                     'July', 'August', 'September', 'October', 'November', 'December'];
  const currentMonthName = monthNames[currentMonth - 1];
  
  const defaulters = residents.filter(resident => {
    const hasPaid = payments.some(p => 
      p.resident_id === resident.id && p.status === 'paid'
    );
    return !hasPaid;
  });

  if (defaulters.length === 0) {
    alert('No pending payments!');
    return;
  }

  const residentIds = defaulters.map(r => r.id);
  const message = `Your maintenance payment of ₹${maintenanceAmount} for ${currentMonthName} ${currentYear} is due. Please pay via GPay: ${gpayNumber}`;
  
  // For now, just show an alert since notificationService isn't imported
  // If you want to use notificationService, import it at the top of the file
  alert(`Ready to send notifications to ${residentIds.length} residents:\n\n${message}`);
  
  // Uncomment below if you want to use actual notification service
 

  
  const result = await notificationService.sendPaymentReminder(
    residentIds,
    message,
    currentMonth,
    currentYear
  );
  
  if (result.success) {
    alert(`Push notifications sent to ${result.count} residents!`);
  } else {
    alert('Failed to send notifications: ' + result.error);
  }

};

  return (
    <div style={{ padding: '20px' }}>
      <h3 style={{ marginBottom: '20px', color: '#333' }}>📱 AI-Powered Payment Reminder Generator</h3>
      
      {/* Statistics */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
        gap: '15px',
        marginBottom: '20px'
      }}>
        <div style={{ background: '#e7f3ff', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#0066cc' }}>{totalFlats}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>Total Flats</div>
        </div>
        <div style={{ background: '#d4edda', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>{paidFlats}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>Paid</div>
        </div>
        <div style={{ background: '#fff3cd', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffc107' }}>{pendingFlats}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>Pending</div>
        </div>
        <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#6c757d' }}>{collectionPercentage}%</div>
          <div style={{ fontSize: '12px', color: '#666' }}>Collected</div>
        </div>
      </div>

      {/* Message Style Selector */}
      <div style={{ 
        background: '#f8f9fa', 
        padding: '20px', 
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <h4 style={{ marginBottom: '15px' }}>🎨 Choose Message Style</h4>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', 
          gap: '10px',
          marginBottom: '15px'
        }}>
          {Object.entries(messageTemplates).map(([key, template]) => (
            <button
              key={key}
              onClick={() => handleStyleChange(key)}
              style={{
                padding: '10px',
                background: messageStyle === key ? '#667eea' : 'white',
                color: messageStyle === key ? 'white' : '#333',
                border: `2px solid ${messageStyle === key ? '#667eea' : '#ddd'}`,
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: messageStyle === key ? '600' : '400',
                transition: 'all 0.2s'
              }}
            >
              {template.name}
            </button>
          ))}
          <button
            onClick={() => handleStyleChange('custom')}
            style={{
              padding: '10px',
              background: messageStyle === 'custom' ? '#667eea' : 'white',
              color: messageStyle === 'custom' ? 'white' : '#333',
              border: `2px solid ${messageStyle === 'custom' ? '#667eea' : '#ddd'}`,
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: messageStyle === 'custom' ? '600' : '400'
            }}
          >
            ✏️ Custom
          </button>
        </div>
        <button
            onClick={generateAIMessage}
            disabled={loading}
            style={{
                padding: '10px 20px',
                background: '#9b59b6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: '600'
            }}
            >
            {loading ? 'Generating...' : '🤖 Generate with AI'}
         </button>
        {/* Custom Template Input */}
        {messageStyle === 'custom' && (
          <div style={{ marginTop: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#666' }}>
              Custom Template (Use placeholders: {'{amount}'}, {'{month}'}, {'{year}'}, {'{gpay}'}, {'{flatDetails}'}, etc.)
            </label>
            <textarea
              value={customTemplate}
              onChange={(e) => {
                setCustomTemplate(e.target.value);
                generateMessage(paymentStatuses, 'custom');
              }}
              placeholder="Enter your custom message template..."
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '13px'
              }}
            />
          </div>
        )}

        {/* Settings */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#666' }}>
              Maintenance Amount (₹)
            </label>
            <input
              type="number"
              value={maintenanceAmount}
              onChange={(e) => {
                setMaintenanceAmount(e.target.value);
                generateMessage(paymentStatuses, messageStyle);
              }}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#666' }}>
              GPay Number
            </label>
            <input
              type="text"
              value={gpayNumber}
              onChange={(e) => {
                setGpayNumber(e.target.value);
                generateMessage(paymentStatuses, messageStyle);
              }}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>
        </div>
      </div>

      {/* Payment Status Checklist */}
      <div style={{ 
        background: 'white', 
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h4>Payment Status Checklist</h4>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => {
                const newStatuses = {};
                Object.keys(paymentStatuses).forEach(flat => {
                  newStatuses[flat] = 'Paid';
                });
                setPaymentStatuses(newStatuses);
                generateMessage(newStatuses, messageStyle);
              }}
              style={{
                padding: '5px 10px',
                background: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Mark All Paid
            </button>
            <button
              onClick={() => {
                const newStatuses = {};
                Object.keys(paymentStatuses).forEach(flat => {
                  newStatuses[flat] = '';
                });
                setPaymentStatuses(newStatuses);
                generateMessage(newStatuses, messageStyle);
              }}
              style={{
                padding: '5px 10px',
                background: '#ffc107',
                color: '#000',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Mark All Pending
            </button>
          </div>
        </div>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', 
          gap: '10px' 
        }}>
          {Object.keys(paymentStatuses).map(flatNumber => (
            <div
              key={flatNumber}
              onClick={() => handleStatusToggle(flatNumber)}
              style={{
                padding: '10px',
                background: paymentStatuses[flatNumber] === 'Paid' ? '#d4edda' : '#f8d7da',
                border: `2px solid ${paymentStatuses[flatNumber] === 'Paid' ? '#c3e6cb' : '#f5c6cb'}`,
                borderRadius: '6px',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.2s'
              }}
            >
              <div style={{ fontWeight: 'bold' }}>{flatNumber}</div>
              <div style={{ 
                fontSize: '10px', 
                marginTop: '5px',
                color: paymentStatuses[flatNumber] === 'Paid' ? '#155724' : '#721c24'
              }}>
                {paymentStatuses[flatNumber] === 'Paid' ? '✅ PAID' : '⏳ PENDING'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Generated Message */}
      <div style={{ 
        background: 'white', 
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <h4 style={{ marginBottom: '15px' }}>📝 Generated Message</h4>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          style={{
            width: '100%',
            minHeight: '350px',
            padding: '15px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            fontSize: '14px',
            lineHeight: '1.5',
            background: '#f8f9fa'
          }}
        />
        
        <div style={{ display: 'flex', gap: '10px', marginTop: '15px', flexWrap: 'wrap' }}>
          <button
            onClick={copyToClipboard}
            style={{
              padding: '10px 20px',
              background: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            📋 Copy to Clipboard
          </button>
          
          <button
            onClick={sendViaWhatsApp}
            style={{
              padding: '10px 20px',
              background: '#25D366',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            📱 Open WhatsApp
          </button>
          
          <button
            onClick={saveReminderHistory}
            disabled={loading}
            style={{
              padding: '10px 20px',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            {loading ? 'Saving...' : '💾 Save to History'}
          </button>
          
          <button
            onClick={() => generateMessage(paymentStatuses, messageStyle)}
            style={{
              padding: '10px 20px',
              background: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            🔄 Regenerate
          </button>
        </div>
      </div>
    </div>
  );
}

export default PaymentReminders;